import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["query", "results", "selectedFiles", "generateButton", "selectAll"]
  static values = { searchUrl: String }

  connect() {
    this.timeout = null
    this.selectedById = new Map()
  }

  search() {
    clearTimeout(this.timeout)
    
    const query = this.queryTarget.value.trim()
    
    if (query.length < 3) {
      this.resultsTarget.innerHTML = ''
      this.updateGenerateButton()
      return
    }

    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
    
    try {
      const response = await fetch(url)
      const data = await response.json()
      
      this.displayResults(data.results)
    } catch (error) {
      console.error('Search failed:', error)
    }
  }

  displayResults(results) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = '<p class="text-muted text-center py-3">No files found</p>'
      return
    }

    const selectAllCheckbox = `
      <div class="list-group-item bg-light">
        <div class="form-check">
          <input class="form-check-input" 
                 type="checkbox" 
                 id="select-all"
                 data-action="change->attachment-search#toggleSelectAll"
                 data-attachment-search-target="selectAll">
          <label class="form-check-label fw-bold" for="select-all">
            Select All (${results.length})
          </label>
        </div>
      </div>
    `

    const html = results.map(result => {
      const isChecked = this.selectedById.has(result.id.toString())
      return `
        <div class="list-group-item">
          <div class="d-flex align-items-start">
            <div class="form-check me-3">
              <input class="form-check-input" 
                     type="checkbox" 
                     id="file-${result.id}"
                     value="${result.id}"
                     data-action="change->attachment-search#toggleFile"
                     data-file-name="${result.name}"
                     data-extension="${result.extension || ''}"
                     data-account-name="${result.account_name}"
                     ${isChecked ? 'checked' : ''}>
            </div>
            <label class="flex-grow-1 cursor-pointer" for="file-${result.id}">
              <h6 class="mb-1">
                <i class="fas ${this.fileIconClass(result.extension)} me-2"></i>
                ${result.name}
              </h6>
              <small class="text-muted">
                <i class="fas fa-building me-1"></i>
                ${result.account_name}
                <span class="mx-2">•</span>
                ${result.size} MB
                <span class="mx-2">•</span>
                ${result.created_at}
              </small>
            </label>
          </div>
        </div>
      `
    }).join('')

    this.resultsTarget.innerHTML = `<div class="list-group">${selectAllCheckbox}${html}</div>`
    this.updateGenerateButton()
  }

  toggleSelectAll(event) {
    const checkboxes = this.resultsTarget.querySelectorAll('input[type="checkbox"]:not(#select-all)')
    
    if (event.target.checked) {
      checkboxes.forEach(cb => {
        cb.checked = true
        this.rememberFile(cb)
      })
    } else {
      checkboxes.forEach(cb => {
        cb.checked = false
        this.selectedById.delete(cb.value)
      })
    }
    
    this.updateGenerateButton()
    this.updateSelectedFilesDisplay()
  }

  toggleFile(event) {
    const checkbox = event.target
    
    if (checkbox.checked) {
      this.rememberFile(checkbox)
    } else {
      this.selectedById.delete(checkbox.value)
    }
    
    this.updateGenerateButton()
    this.updateSelectedFilesDisplay()
    this.updateSelectAllCheckbox()
  }

  updateSelectAllCheckbox() {
    if (this.hasSelectAllTarget) {
      const checkboxes = this.resultsTarget.querySelectorAll('input[type="checkbox"]:not(#select-all)')
      const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length
      
      this.selectAllTarget.checked = checkboxes.length > 0 && checkedCount === checkboxes.length
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length
    }
  }

  updateSelectedFilesDisplay() {
    if (!this.hasSelectedFilesTarget) return

    const count = this.selectedById.size
    
    if (count === 0) {
      this.selectedFilesTarget.innerHTML = ''
      return
    }

    const fileNames = Array.from(this.selectedById.values())
      .map(file => {
        return `<li>${file.name} <span class="text-muted">(${file.accountName})</span></li>`
      })
      .join('')

    this.selectedFilesTarget.innerHTML = `
      <div class="alert alert-info">
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-grow-1">
            <h6 class="mb-2">
              <i class="fas fa-check-circle me-2"></i>
              ${count} file${count === 1 ? '' : 's'} selected
            </h6>
            <ul class="mb-0 small">${fileNames}</ul>
          </div>
          <button type="button" 
                  class="btn-close ms-2" 
                  data-action="click->attachment-search#clearSelection"></button>
        </div>
      </div>
    `
  }

  fileIconClass(extension) {
    switch ((extension || '').toLowerCase()) {
      case 'xlsx':
      case 'xls':
        return 'fa-file-excel text-success'
      case 'csv':
        return 'fa-file-csv text-info'
      case 'docx':
      case 'doc':
        return 'fa-file-word text-primary'
      case 'pdf':
        return 'fa-file-pdf text-danger'
      default:
        return 'fa-file text-muted'
    }
  }

  rememberFile(checkbox) {
    this.selectedById.set(checkbox.value, {
      name: checkbox.dataset.fileName,
      accountName: checkbox.dataset.accountName,
      extension: (checkbox.dataset.extension || '').toLowerCase()
    })
  }

  selectedHasPdf() {
    return Array.from(this.selectedById.values()).some((file) => file.extension === 'pdf')
  }

  updateGenerateButton() {
    if (!this.hasGenerateButtonTarget) return

    const count = this.selectedById.size
    
    if (count === 0) {
      this.generateButtonTarget.classList.add('d-none')
      return
    }

    this.generateButtonTarget.classList.remove('d-none')
    this.generateButtonTarget.disabled = !this.selectedHasPdf()
    this.generateButtonTarget.innerHTML = `
      <i class="fas fa-magic me-2"></i>
      Generate Quote with AI
    `
  }

  clearSelection() {
    this.selectedById.clear()
    this.resultsTarget.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.checked = false
    })
    this.updateGenerateButton()
    this.updateSelectedFilesDisplay()
  }

  generateQuotes(event) {
    const fileIds = Array.from(this.selectedById.keys())
    
    if (fileIds.length === 0 || !this.selectedHasPdf()) {
      alert('Please select at least one tax return PDF')
      return
    }

    // Show loading state
    const button = event.currentTarget
    button.disabled = true
    button.innerHTML = `<i class="fas fa-spinner fa-spin me-2"></i>Generating quote...`

    // Submit bulk creation request
    fetch('/admin/ai/tax_quotes/bulk_create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        storage_file_ids: fileIds
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Redirect to index page with success message
        window.location.href = data.redirect_url || '/admin/ai/tax_quotes'
      } else {
        alert('Error: ' + (data.error || 'Unknown error'))
        button.disabled = false
        this.updateGenerateButton()
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Failed to generate quotes')
      button.disabled = false
      this.updateGenerateButton()
    })
  }
}

